import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { AuthCard, AuthCardTitle, AuthFormError, AuthTextButton } from './AuthCard';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface LoginProps {
  onSwitchToSignup: () => void;
}

export const Login: React.FC<LoginProps> = ({ onSwitchToSignup }) => {
  const { login } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (searchParams.get('accountDisabled') === '1') {
      setError(t('account_disabled_login_message'));
      const next = new URLSearchParams(searchParams);
      next.delete('accountDisabled');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login({ email, password });
    } catch (err) {
      const code = err instanceof Error ? (err as Error & { errorCode?: string }).errorCode : undefined;
      setError(
        code === 'ACCOUNT_DISABLED'
          ? t('account_disabled_login_message')
          : err instanceof Error
            ? err.message
            : t('login_failed')
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthCard>
      <AuthCardTitle>{t('login_title')}</AuthCardTitle>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <Label htmlFor="email" className="mb-2 block">
            {t('email')}
          </Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        <div className="mb-6">
          <Label htmlFor="password" className="mb-2 block">
            {t('password')}
          </Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        {error && <AuthFormError>{error}</AuthFormError>}
        <Button type="submit" variant="inverse" className="mb-4 w-full" disabled={isLoading}>
          {isLoading ? t('logging_in') : t('login')}
        </Button>
        <div className="mb-4 text-center">
          <AuthTextButton onClick={() => navigate('/forgot-password')}>
            {t('forgot_password')}
          </AuthTextButton>
        </div>
        <div className="text-center text-sm text-muted-foreground">
          {t('dont_have_account')}{' '}
          <AuthTextButton onClick={onSwitchToSignup}>
            {t('sign_up')}
          </AuthTextButton>
        </div>
      </form>
    </AuthCard>
  );
};
